import { useMemo } from 'react'
import { useQueries, useQuery, useQueryClient } from '@tanstack/react-query'
import { protectPendingOptionItems } from './option.cache'
import { useStableCallback } from '@/hooks/useStableCallback'
import type { Language } from '@/types'
import type { DeletedOptionIdsByKey } from '@/types/grid'
import {
  buildFlatFromViewTree,
  buildViewTreeFromNormalized,
  FieldOptionsViewData,
  normalizeFieldOptionsFromFieldItem,
  SelectOption,
  toSelectOptions,
  ViewOption,
} from './option.normalized'
import { buildRefDataById } from './option.utils'
import {
  FieldItem,
  FieldOwnerItem,
  OptionItem,
  ProductStatusKey,
  PropertyDefinitionNormalized,
  SupportedKeys,
  useGetProperty,
} from '@/services/property'
import { fetchAllOptionsByField } from './option.query'
import {
  getOptionFilterHash,
  listQueryKey,
  type OptionAudience,
  optionsByFieldQueryKey,
} from './property.keys'
import type { OptionFieldFilter } from '@/services/queries/utils/filters'
import { toOptionListFromItems } from './property.adapter'
import { optionQueryPreset, staticQueryPreset } from '@/services/query-presets'
import { getFieldOwnerItem } from './field.query'
import { useUserContext } from '@/context'
import { useSelectedOrganizationTeams } from '@/hooks/useSelectedOrganizationTeams'
import {
  buildOptionAccessPolicy,
  isOptionSelectable,
  isOptionVisibleInEditor,
  type OptionAccessPolicy,
  type ResolvedOptionListContext,
  resolveOptionListContext,
} from './option.access'

type OptionListRequest = {
  requestId: string
  audience: Exclude<OptionAudience, 'moderation'>
  listId: string
  optionFilter?: Record<string, unknown>
}

const buildOptionListRequestId = ({
  audience,
  listId,
  optionFilter,
}: {
  audience: OptionAudience
  listId: string
  optionFilter?: Record<string, unknown>
}) => `${listId}:${audience}:${getOptionFilterHash(optionFilter)}`

const OPTIONS_BY_FIELD_QUERY_SCHEMA_VERSION = 'options-with-system-metadata-v1'

const collectDeletedOptionIds = (items: OptionItem[]): string[] => {
  const ids = new Set<string>()
  const visit = (item: OptionItem) => {
    if (item.status === 'DELETED') ids.add(item.id)
    for (const child of item.options?.items ?? []) visit(child)
  }
  items.forEach(visit)
  return [...ids].sort()
}

const selectOptionsByField = ({
  items,
  supportedKey,
  optionId,
  key,
  lang,
}: {
  items: OptionItem[]
  supportedKey: SupportedKeys | undefined
  optionId: string | undefined
  key: string | undefined
  lang: Language | undefined
}): FieldOptionsViewData => {
  if (!supportedKey) {
    throw new Error('selectOptionsByField: supportedKey is required')
  }
  if (!optionId) {
    throw new Error('selectOptionsByField: optionId is required')
  }
  if (!key) {
    throw new Error('selectOptionsByField: key is required')
  }

  const normalized = normalizeFieldOptionsFromFieldItem({
    id: optionId,
    key,
    options: { items },
  } as FieldItem)
  const options = buildViewTreeFromNormalized(normalized, lang)
  const flatOptions = buildFlatFromViewTree(options)
  const refDataById = buildRefDataById(flatOptions)
  const selectOptions = toSelectOptions(options, key)

  return {
    optionsById: normalized.optionsById,
    supportedKey,
    options,
    flatOptions,
    refDataById,
    selectOptions,
  }
}

type ListOwnerQueryState = {
  data: FieldOwnerItem | undefined
  isPending: boolean
}

type ResolvedPropertyOptionLists = {
  property: PropertyDefinitionNormalized | undefined
  organizationFamilyIds: string[]
  listContexts: ResolvedOptionListContext[]
  listContextsByKey: Record<string, ResolvedOptionListContext>
  listContextsByListId: Record<string, ResolvedOptionListContext>
  policiesByKey: Record<string, OptionAccessPolicy>
  supportedKeys: SupportedKeys[]
  supportedKeysByKey: Record<string, SupportedKeys>
  refKey: string
  definitionVersion: string
  isCustomProperty: boolean
  isPending: boolean
  isReady: boolean
}

const createFallbackSupportedKey = ({
  key,
  optionId,
  listOwner,
}: {
  key: string
  optionId: string
  listOwner?: FieldOwnerItem
}): SupportedKeys => ({
  id: optionId,
  propertyKey: key,
  key,
  label: listOwner?.label ?? key,
  baseLabel: listOwner?.label ?? key,
  value: listOwner?.value ?? optionId,
  listId: optionId,
  type: listOwner?.type ?? 'LIST',
  isMulti: false,
  isRef: false,
  isID: false,
  isPrivate: false,
  isLocked: false,
  organizationID: listOwner?.organizationFieldsId,
  organizationName: listOwner?.organization?.name,
})

const filterConsumerOptionItems = (
  items: OptionItem[],
  audience: 'display' | 'picker',
): OptionItem[] =>
  items.flatMap((item) => {
    const children = filterConsumerOptionItems(
      item.options?.items ?? [],
      audience,
    )
    const visible =
      audience === 'display'
        ? isOptionVisibleInEditor(item)
        : isOptionSelectable(item)
    return visible
      ? [
          {
            ...item,
            ...(item.options
              ? { options: { ...item.options, items: children } }
              : {}),
          },
        ]
      : children
  })

const buildDisplaySelectOptions = ({
  options,
  propertyKey,
  selectableIds,
}: {
  options: ViewOption[]
  propertyKey: string
  selectableIds: Set<string>
}): SelectOption[] => {
  const filterNodes = (nodes: ViewOption[]): ViewOption[] =>
    nodes
      .filter((node) => isOptionVisibleInEditor(node))
      .map((node) => {
        const children = node.options ? filterNodes(node.options) : []
        const { options: _ignoredOptions, ...rest } = node

        return children.length > 0
          ? {
              ...rest,
              options: children,
            }
          : {
              ...rest,
            }
      })

  const visibleOptions = filterNodes(options)
  const selectOptions = toSelectOptions(visibleOptions, propertyKey)

  const decorate = (nodes: SelectOption[]): SelectOption[] =>
    nodes.map((node) => {
      const children = node.options ? decorate(node.options) : []

      return children.length > 0
        ? {
            ...node,
            isSelectable: isOptionSelectable({
              id: node.id,
              selectableIds,
            }),
            options: children,
          }
        : {
            ...node,
            isSelectable: isOptionSelectable({
              id: node.id,
              selectableIds,
            }),
          }
    })

  return decorate(selectOptions)
}

const mergeResolvedListContextIntoSupportedKey = ({
  supportedKey,
  listContext,
}: {
  supportedKey: SupportedKeys
  listContext?: ResolvedOptionListContext
}): SupportedKeys => {
  if (!listContext) return supportedKey

  return {
    ...supportedKey,
    listId: listContext.listId,
    listOwnerOrganizationID: listContext.ownerOrganizationID,
    listOwnerOrganizationName: listContext.ownerOrganizationName,
    sourceKind: listContext.sourceKind,
    isShared: listContext.isShared,
  }
}

const combineResolvedPropertyOptionLists = ({
  isPropertyPending,
  ownerQueries,
  property,
  activeOrganizationId,
  organizationFamilyIds,
  scopeKind,
  userID,
  isAdmin,
  isGuest,
  canCreate,
  canUpdate,
}: {
  property: PropertyDefinitionNormalized | undefined
  isPropertyPending: boolean
  ownerQueries: ListOwnerQueryState[]
  activeOrganizationId?: string
  organizationFamilyIds?: readonly string[]
  scopeKind?: Parameters<typeof resolveOptionListContext>[0]['scopeKind']
  userID?: string
  isAdmin?: boolean
  isGuest?: boolean
  canCreate?: boolean
  canUpdate?: boolean
}): ResolvedPropertyOptionLists => {
  const ownerQueriesById = new Map<string, FieldOwnerItem>()

  ownerQueries.forEach((query) => {
    if (!query.data?.id) return
    ownerQueriesById.set(query.data.id, query.data)
  })

  const listContexts = (property?.listDefinitions ?? []).map((definition) => {
    const ownerData =
      definition.listId === definition.propertyFieldId
        ? undefined
        : ownerQueriesById.get(definition.listId)

    return resolveOptionListContext({
      ...definition,
      label:
        definition.listId === definition.propertyFieldId
          ? definition.label
          : (ownerData?.label ?? definition.label),
      activeOrganizationID: activeOrganizationId,
      scopeKind,
      ownerOrganizationID:
        definition.listId === definition.propertyFieldId
          ? definition.propertyOrganizationID
          : ownerData?.organizationFieldsId,
      ownerOrganizationName:
        definition.listId === definition.propertyFieldId
          ? property?.byKey?.[definition.key]?.organizationName
          : ownerData?.organization?.name,
    })
  })

  const listContextsByKey = Object.fromEntries(
    listContexts.map((listContext) => [listContext.key, listContext]),
  )
  const listContextsByListId = Object.fromEntries(
    listContexts.map((listContext) => [listContext.listId, listContext]),
  )
  const policiesByKey = Object.fromEntries(
    listContexts.map((listContext) => [
      listContext.key,
      buildOptionAccessPolicy({
        list: listContext,
        userID,
        organizationFamilyIds,
        isAdmin,
        isGuest,
        canCreate,
        canUpdate,
      }),
    ]),
  )
  const supportedKeys = (property?.supportedKeys ?? []).map((supportedKey) =>
    mergeResolvedListContextIntoSupportedKey({
      supportedKey,
      listContext: listContextsByKey[supportedKey.key],
    }),
  )
  const supportedKeysByKey = Object.fromEntries(
    supportedKeys.map((supportedKey) => [supportedKey.key, supportedKey]),
  )
  const isOwnersPending = ownerQueries.some((query) => query.isPending)
  const hasResolvedProperty = !isPropertyPending && property !== undefined
  const isPending =
    isPropertyPending || (hasResolvedProperty && isOwnersPending)
  const isReady = hasResolvedProperty && !isOwnersPending

  return {
    property,
    organizationFamilyIds: [...(organizationFamilyIds ?? [])],
    listContexts,
    listContextsByKey,
    listContextsByListId,
    policiesByKey,
    supportedKeys,
    supportedKeysByKey,
    refKey: property?.refKey ?? '',
    definitionVersion: property?.definitionVersion ?? '',
    isCustomProperty: property?.isCustomProperty ?? false,
    isPending,
    isReady,
  }
}

export const useResolvedPropertyOptionLists = (
  lang?: Language,
): ResolvedPropertyOptionLists => {
  const { data: property, isPending: isPropertyPending } = useGetProperty({
    lang,
  })
  const { authorization, ownership, organizationScope } = useUserContext()
  const ownerQueries = useQueries({
    queries: Array.from(
      new Set(
        (property?.listDefinitions ?? [])
          .filter(
            (definition) => definition.listId !== definition.propertyFieldId,
          )
          .map((definition) => definition.listId),
      ),
    ).map((listId) => ({
      ...staticQueryPreset,
      queryKey: ['field-owner', listId],
      queryFn: () => getFieldOwnerItem(listId),
      enabled: Boolean(listId),
    })),
    combine(results) {
      return results.map((result) => ({
        data: result.data,
        isPending: result.isPending,
      }))
    },
  })
  const userID = authorization?.user?.id ?? ownership?.byUserID
  const isAdmin = authorization?.user?.role === 'ADMIN'
  const isGuest = authorization?.user?.role === 'GUEST'
  const canCreate = authorization?.optionsAuth?.canCreate
  const canUpdate = authorization?.optionsAuth?.canUpdate
  const { data: selectedOrganizationTeams = [] } =
    useSelectedOrganizationTeams()
  const organizationFamilyIds = useMemo(
    () => [
      ...new Set(
        [
          organizationScope.activeOrganizationId,
          ...selectedOrganizationTeams.map((team) => team.value),
        ].filter((organizationId): organizationId is string =>
          Boolean(organizationId),
        ),
      ),
    ],
    [organizationScope.activeOrganizationId, selectedOrganizationTeams],
  )

  return useMemo(
    () =>
      combineResolvedPropertyOptionLists({
        property,
        isPropertyPending,
        ownerQueries,
        activeOrganizationId: organizationScope.activeOrganizationId,
        organizationFamilyIds,
        scopeKind: organizationScope.scopeKind,
        userID,
        isAdmin,
        isGuest,
        canCreate,
        canUpdate,
      }),
    [
      property,
      isPropertyPending,
      ownerQueries,
      organizationScope.activeOrganizationId,
      organizationFamilyIds,
      organizationScope.scopeKind,
      userID,
      isAdmin,
      isGuest,
      canCreate,
      canUpdate,
    ],
  )
}

export const useOptionsByField = (params: {
  key: string
  optionId?: string
  lang?: Language
  optionFilter?: OptionFieldFilter
  supportedKey?: SupportedKeys
}) => {
  const queryClient = useQueryClient()
  const { key, optionId, lang, optionFilter: explicitOptionFilter } = params
  const { data: property } = useGetProperty({ lang })

  const optionFilter =
    'optionFilter' in params ? explicitOptionFilter : property?.optionFilter
  const supportedKey =
    params.supportedKey ??
    property?.byKey?.[key] ??
    (optionId
      ? createFallbackSupportedKey({
          key,
          optionId,
        })
      : undefined)

  return useQuery({
    ...optionQueryPreset,
    queryKey: [
      ...optionsByFieldQueryKey(optionId, lang, 'moderation', optionFilter),
      OPTIONS_BY_FIELD_QUERY_SCHEMA_VERSION,
      optionFilter,
    ] as const,
    queryFn: () =>
      fetchAllOptionsByField({
        fieldOptionsId: optionId,
        lang,
        optionFilter,
      }).then((items) =>
        protectPendingOptionItems(queryClient, optionId, items),
      ),
    enabled: !!optionId && !!supportedKey,
    select: (items) =>
      selectOptionsByField({ items, supportedKey, optionId, key, lang }),
  })
}

type OptionListResult = ReturnType<typeof toOptionListFromItems>

type ListOptionsQueryState = {
  requestId: string
  listId: string
  audience: Exclude<OptionAudience, 'moderation'>
  data: OptionItem[] | undefined
  isPending: boolean
}

export type UseListOptionsResult = {
  isPending: boolean
  isReady: boolean
  options: Partial<Record<string, OptionListResult['options']>>
  optionsByID: Partial<Record<string, OptionListResult['optionsByKey']>>
  refDataByKey: Partial<Record<ProductStatusKey, Record<string, string>>>
  deletedOptionIdsByKey?: DeletedOptionIdsByKey
  selectOptions: Partial<Record<string, OptionListResult['selectOptions']>>
  displaySelectOptions: Partial<Record<string, SelectOption[]>>
  canCreateInListByKey: Partial<Record<string, boolean>>
  supportedKeys: SupportedKeys[]
  supportedKeysByKey: Record<string, SupportedKeys>
  listContextsByKey: Record<string, ResolvedOptionListContext>
  refKey: string
  definitionVersion: string
  isCustomProperty: boolean
}

export const combineListOptionsResult = ({
  lang,
  resolvedProperty,
  queryResults,
}: {
  lang?: Language
  resolvedProperty: ResolvedPropertyOptionLists
  queryResults: ListOptionsQueryState[]
}): UseListOptionsResult => {
  const resultsByRequestId = new Map<string, OptionItem[]>()
  const optionListResultsByProjectionId = new Map<string, OptionListResult>()

  queryResults.forEach((result) => {
    if (!Array.isArray(result.data)) return
    resultsByRequestId.set(result.requestId, result.data)
  })

  const combined = resolvedProperty.listContexts.reduce<{
    options: Partial<Record<string, OptionListResult['options']>>
    optionsByID: Partial<Record<string, OptionListResult['optionsByKey']>>
    refDataByKey: Partial<Record<ProductStatusKey, Record<string, string>>>
    deletedOptionIdsByKey: DeletedOptionIdsByKey
    selectOptions: Partial<Record<string, OptionListResult['selectOptions']>>
    displaySelectOptions: Partial<Record<string, SelectOption[]>>
    canCreateInListByKey: Partial<Record<string, boolean>>
  }>(
    (acc, listContext) => {
      const policy = resolvedProperty.policiesByKey[listContext.key]
      const buildRequest = (
        audience: Exclude<OptionAudience, 'moderation'>,
      ): OptionListRequest => ({
        requestId: buildOptionListRequestId({
          audience,
          listId: listContext.listId,
          optionFilter:
            audience === 'display'
              ? policy?.displayLookupFilter
              : policy?.pickerFilter,
        }),
        audience,
        listId: listContext.listId,
        optionFilter:
          audience === 'display'
            ? policy?.displayLookupFilter
            : policy?.pickerFilter,
      })
      const displayRequest = buildRequest('display')
      const pickerRequest = buildRequest('picker')
      const getOptionListResult = (request: OptionListRequest) => {
        const projectionId = `${request.requestId}:${listContext.key}`
        const existing = optionListResultsByProjectionId.get(projectionId)
        if (existing) return existing
        const items = resultsByRequestId.get(request.requestId) ?? []
        const next = toOptionListFromItems({
          key: listContext.key,
          items: filterConsumerOptionItems(items, request.audience),
          lang,
        })
        optionListResultsByProjectionId.set(projectionId, next)
        return next
      }
      const display = getOptionListResult(displayRequest)
      const picker = getOptionListResult(pickerRequest)
      acc.deletedOptionIdsByKey[listContext.key] = collectDeletedOptionIds(
        resultsByRequestId.get(displayRequest.requestId) ?? [],
      )
      const selectableIds = new Set(
        picker?.flatOptions
          ?.map((option) => option.id)
          .filter((id): id is string => typeof id === 'string') ?? [],
      )

      acc.options[listContext.key] = display?.options ?? []
      acc.optionsByID[listContext.key] = display?.optionsByKey ?? {}
      acc.refDataByKey[listContext.key] = display?.refDataById ?? {}
      acc.selectOptions[listContext.key] = picker?.selectOptions ?? []
      acc.displaySelectOptions[listContext.key] = buildDisplaySelectOptions({
        options: display?.options ?? [],
        propertyKey: listContext.key,
        selectableIds,
      })
      acc.canCreateInListByKey[listContext.key] =
        policy?.canCreateInList ?? false
      return acc
    },
    {
      options: {},
      optionsByID: {},
      refDataByKey: {},
      deletedOptionIdsByKey: {},
      selectOptions: {},
      displaySelectOptions: {},
      canCreateInListByKey: {},
    },
  )

  const isListsPending = queryResults.some((result) => result.isPending)
  const hasResolvedProperty = resolvedProperty.isReady
  const isPending =
    resolvedProperty.isPending || (hasResolvedProperty && isListsPending)
  const isReady = hasResolvedProperty && !isListsPending

  return {
    isPending,
    isReady,
    options: combined.options,
    optionsByID: combined.optionsByID,
    refDataByKey: combined.refDataByKey,
    deletedOptionIdsByKey: combined.deletedOptionIdsByKey,
    selectOptions: combined.selectOptions,
    displaySelectOptions: combined.displaySelectOptions,
    canCreateInListByKey: combined.canCreateInListByKey,
    supportedKeys: resolvedProperty.supportedKeys,
    supportedKeysByKey: resolvedProperty.supportedKeysByKey,
    listContextsByKey: resolvedProperty.listContextsByKey,
    refKey: resolvedProperty.refKey,
    definitionVersion: resolvedProperty.definitionVersion,
    isCustomProperty: resolvedProperty.isCustomProperty,
  }
}
// -----------------------------------------------------------------------------
// Hook: useListOptions
// - Récupère toutes les LIST d’une propriété (via useGetProperty.lists)
// - Pour chaque LIST ([key, id]) => fetchAllOptionsByField + toOptionListFromItems
// - Combine les résultats dans un format prêt à l'emploi
//   pour indexation par key et par id.
// -----------------------------------------------------------------------------

export const useListOptions = (
  lang?: Language,
): UseListOptionsResult & {
  refreshListOptions: (key: string) => Promise<UseListOptionsResult | undefined>
} => {
  const queryClient = useQueryClient()
  const resolvedProperty = useResolvedPropertyOptionLists(lang)
  const queryRequests = Array.from(
    resolvedProperty.listContexts
      .reduce<Map<string, OptionListRequest>>((acc, listContext) => {
        const policy = resolvedProperty.policiesByKey[listContext.key]
        if (!policy?.displayLookupFilter || !policy?.pickerFilter) {
          return acc
        }

        const requests: OptionListRequest[] = [
          {
            requestId: buildOptionListRequestId({
              audience: 'display',
              listId: listContext.listId,
              optionFilter: policy.displayLookupFilter,
            }),
            audience: 'display',
            listId: listContext.listId,
            optionFilter: policy.displayLookupFilter,
          },
          {
            requestId: buildOptionListRequestId({
              audience: 'picker',
              listId: listContext.listId,
              optionFilter: policy.pickerFilter,
            }),
            audience: 'picker',
            listId: listContext.listId,
            optionFilter: policy.pickerFilter,
          },
        ]

        requests.forEach((request) => {
          if (!acc.has(request.requestId)) {
            acc.set(request.requestId, request)
          }
        })

        return acc
      }, new Map())
      .values(),
  )

  const queries = useQueries({
    queries: queryRequests.map((request) => ({
      ...optionQueryPreset,
      // Shared-list ownership changes the audience scope. Do not fetch a
      // provisional scope and immediately fetch the same list again.
      enabled: resolvedProperty.isReady && Boolean(request.listId),
      queryKey: listQueryKey(
        request.listId,
        lang,
        request.audience,
        request.optionFilter as Record<string, unknown> | undefined,
      ),
      queryFn: () =>
        fetchAllOptionsByField({
          fieldOptionsId: request.listId,
          lang,
          optionFilter: request.optionFilter,
          includeDeletedWitnesses: request.audience === 'display',
        }).then((items) =>
          protectPendingOptionItems(queryClient, request.listId, items),
        ),
    })),
    /**
     * `combine` nous permet de renvoyer une structure compacte,
     * déjà indexée pour éviter du compute dans les composants.
     */
    combine(result) {
      return combineListOptionsResult({
        lang,
        resolvedProperty,
        queryResults: result.map((query, index) => ({
          data: query.data,
          isPending: query.isPending,
          requestId: queryRequests[index]?.requestId ?? '',
          listId: queryRequests[index]?.listId ?? '',
          audience: queryRequests[index]?.audience ?? 'display',
        })),
      })
    },
  })

  const refreshListOptions = useStableCallback(async (key: string) => {
    if (!resolvedProperty.isReady) return undefined
    const list = resolvedProperty.listContextsByKey[key]
    const requests = queryRequests.filter(
      (request) => request.listId === list?.listId,
    )
    if (!list || !requests.length) return undefined
    await Promise.all(
      requests.map((request) =>
        queryClient.fetchQuery({
          ...optionQueryPreset,
          staleTime: 0,
          queryKey: listQueryKey(
            request.listId,
            lang,
            request.audience,
            request.optionFilter,
          ),
          queryFn: () =>
            fetchAllOptionsByField({
              fieldOptionsId: request.listId,
              lang,
              optionFilter: request.optionFilter,
              includeDeletedWitnesses: request.audience === 'display',
            }).then((items) =>
              protectPendingOptionItems(queryClient, request.listId, items),
            ),
        }),
      ),
    )
    return combineListOptionsResult({
      lang,
      resolvedProperty,
      queryResults: queryRequests.map((request) => {
        const data = queryClient.getQueryData<OptionItem[]>(
          listQueryKey(
            request.listId,
            lang,
            request.audience,
            request.optionFilter,
          ),
        )
        return { ...request, data, isPending: data === undefined }
      }),
    })
  })
  return {
    isPending: queries.isPending,
    isReady: queries.isReady,
    options: queries.options,
    optionsByID: queries.optionsByID,
    refDataByKey: queries.refDataByKey,
    deletedOptionIdsByKey: queries.deletedOptionIdsByKey,
    selectOptions: queries.selectOptions,
    displaySelectOptions: queries.displaySelectOptions,
    canCreateInListByKey: queries.canCreateInListByKey,
    supportedKeys: queries.supportedKeys,
    supportedKeysByKey: queries.supportedKeysByKey,
    listContextsByKey: queries.listContextsByKey,
    refKey: queries.refKey,
    definitionVersion: queries.definitionVersion,
    isCustomProperty: queries.isCustomProperty,
    refreshListOptions,
  }
}
